import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  selectAccessLevel,
  selectSubscription,
} from '../../../../slices/subscriptions/subscriptionsSlice';
import { useNavigate } from 'react-router-dom';

import { handleCheckout } from '../../../../services/subscriptions';
import LightStreak from '../../../../assets/images/light-streak.png';
import {
  SubscriptionPlan,
  SubscriptionAccess,
  AnnualBilling,
  hasComplimentaryOnlyAccess,
} from '../../../../types/Subscription';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { capture } from '../../../../utils/analytics';
import OraGuarantee from '../../../../assets/svgs/og.svg';
import { DiscountResponse } from '../../../../services/subscriptions';
import {
  PRE_CLINICAL_PRODUCT_NAME,
  STEP_SHELF_PRODUCT_NAME,
} from '../../../../constants/products';
import React from 'react';
import { CheckCircle, Check } from '@phosphor-icons/react';
import { computeDiscountedPrice, formatPrice } from '../../../../utils/pricing';

interface PricingOptionProps {
  isAuthenticated: boolean;
  title: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  features: React.ReactNode;
  savings?: string;
  savingPercent?: string;
  isGroupOption?: boolean;
  product: SubscriptionAccess;
  plan: SubscriptionPlan;
  guarantee: boolean;
  activeDiscount?: DiscountResponse | null;
  setUpgradeModalOpen: (open: boolean) => void;
}

const PricingOption: React.FC<PricingOptionProps> = ({
  isAuthenticated,
  title,
  description,
  monthlyPrice,
  annualPrice,
  features,
  savingPercent,
  isGroupOption,
  product,
  plan,
  guarantee,
  activeDiscount,
  setUpgradeModalOpen,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const subscription = useSelector(selectSubscription);
  const rawAccessLevel = useSelector(selectAccessLevel);

  // Complimentary-only users (e.g. a support grant on top of a canceled
  // subscription) get the same purchase options as free users so they can
  // (re)subscribe while the grant is active.
  const accessLevel = hasComplimentaryOnlyAccess(subscription)
    ? SubscriptionAccess.FREE
    : rawAccessLevel;

  const primaryExamAccessLevel = useSelector(
    examsSelectors.selectPrimaryExamAccessLevel
  );

  const navigate = useNavigate();

  const renderFeatures = (features: React.ReactNode, guarantee: boolean) => {
    return (
      <div className="pricing-option_inner_bottom_features">
        <h3>
          <span>Features</span>
          <span className="line"></span>
        </h3>
        <ul className="features-list">
          {features}
          {guarantee && (
            <li className="guarantee-item">
              <Check />
              <button
                onClick={() => {
                  const guaranteeElement = document.getElementById('guarantee');
                  if (guaranteeElement) {
                    guaranteeElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <img
                  className="ora-guarantee"
                  src={OraGuarantee}
                  alt="Ora Guarantee"
                  aria-label="Ora Guarantee"
                />
              </button>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const getBasePriceForSelectedPlan = (): number => {
    const raw = plan === 'ANNUAL' ? annualPrice : monthlyPrice;
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) return 0;
    return parsed;
  };

  const basePrice = getBasePriceForSelectedPlan();
  const discountedPrice = computeDiscountedPrice(basePrice, activeDiscount);
  const isDiscountActive = discountedPrice < basePrice;
  const displayPrice = isDiscountActive ? discountedPrice : basePrice;
  const displayPriceStr = formatPrice(displayPrice);
  const hasSubscription = accessLevel !== SubscriptionAccess.FREE;
  const isUpgrading = accessLevel === SubscriptionAccess.PRE_CLINICAL;

  const renderCheckoutButton = () => {
    const renderCheckoutButtonText = () => {
      if (isAuthenticated) {
        // Free User (No Active Subscription)
        if (accessLevel === SubscriptionAccess.FREE) {
          if (product === SubscriptionAccess.PRE_CLINICAL) {
            return 'Get Started';
          } else if (product === SubscriptionAccess.STEP_SHELF) {
            return 'Get Started';
          } else {
            return 'Current Plan';
          }
        } else {
          // User Has Active Preclinical Subscription
          if (accessLevel === SubscriptionAccess.PRE_CLINICAL) {
            if (product === SubscriptionAccess.PRE_CLINICAL) {
              return 'Current Plan';
            } else if (product === SubscriptionAccess.STEP_SHELF) {
              return `Upgrade to ${STEP_SHELF_PRODUCT_NAME}`;
            } else {
              return 'Free Forever';
            }
          } else {
            if (product === SubscriptionAccess.PRE_CLINICAL) {
              return `${PRE_CLINICAL_PRODUCT_NAME} Plan`;
            } else if (product === SubscriptionAccess.STEP_SHELF) {
              return 'Current Plan';
            } else {
              return 'Free Forever';
            }
          }
        }
      } else {
        if (monthlyPrice === '0') {
          return 'Free Forever';
        } else {
          return 'Get Started';
        }
      }
    };

    const disableCheckoutButton =
      Boolean(
        (accessLevel === SubscriptionAccess.PRE_CLINICAL &&
          monthlyPrice === '0') ||
        (accessLevel === SubscriptionAccess.PRE_CLINICAL &&
          product === SubscriptionAccess.PRE_CLINICAL)
      ) || accessLevel === SubscriptionAccess.STEP_SHELF;

    return (
      <button
        className="button button--get-started"
        onClick={() => {
          if (isGroupOption) {
            navigate('/group');
          } else if (isAuthenticated) {
            if (isUpgrading && product === SubscriptionAccess.STEP_SHELF) {
              capture('pricing_upgrade_to_pro_clicked');
              setUpgradeModalOpen(true);
            } else {
              if (plan === SubscriptionPlan.ANNUAL) {
                const annualBilling = AnnualBilling.BILLED_YEARLY;

                capture('pricing_option_clicked', {
                  product,
                  plan,
                  annualBilling,
                });

                capture('checkout_start', {
                  product,
                  plan,
                  annualBilling,
                  source: 'pricing_page',
                });

                handleCheckout(product, plan, annualBilling);
              } else {
                capture('pricing_option_clicked', {
                  product,
                  plan,
                });

                capture('checkout_start', {
                  product,
                  plan,
                  source: 'pricing_page',
                });

                handleCheckout(product, plan);
              }
            }
          } else {
            if (plan === SubscriptionPlan.ANNUAL) {
              const annualBilling = AnnualBilling.BILLED_YEARLY;

              capture('pricing_option_clicked', {
                product,
                plan,
                annualBilling,
              });

              const nonAuthCheckoutLink = `/auth?signup=true&product=${product}&plan=${plan}&annualBilling=${annualBilling}`;
              navigate(nonAuthCheckoutLink);
            } else {
              const nonAuthCheckoutLink = `/auth?signup=true&product=${product}&plan=${plan}`;
              navigate(nonAuthCheckoutLink);
            }
          }
        }}
        disabled={monthlyPrice === '0' || disableCheckoutButton}
      >
        {renderCheckoutButtonText()}
      </button>
    );
  };

  const renderDescription = () => {
    if (isDiscountActive) {
      return (
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexWrap: 'wrap',
          }}
        >
          {description}
          <span className="discount-applied">
            <CheckCircle weight="fill" />
            Discount Applied
          </span>
        </p>
      );
    }
    if (description) {
      return <p>{description}</p>;
    }
    return null;
  };

  return (
    <div
      className={`pricing-option pricing-option${currentUser ? ' pricing-option--authed' : ''}${
        product === primaryExamAccessLevel && !isUpgrading && !hasSubscription
          ? ' pricing-option--primary'
          : ''
      }`}
    >
      <div className="pricing-option_inner">
        {savingPercent &&
          plan === 'ANNUAL' &&
          !isUpgrading &&
          !hasSubscription && (
            <div className="savings-pill">Save {savingPercent}</div>
          )}
        <div className="pricing-option_inner_top">
          <h2>{title}</h2>
          {renderDescription()}
          <div className="pricing-option_inner_top_price">
            <div className="pricing-option_inner_top_price_amount">
              {isDiscountActive ? (
                <div className="discounted-price-container">
                  <div className="price-original">
                    ${plan === 'ANNUAL' ? annualPrice : monthlyPrice}
                  </div>
                  <div className="price-discounted">
                    <span className="dollar-sign">$</span>
                    {displayPriceStr}
                  </div>
                </div>
              ) : (
                <>
                  <span className="dollar-sign">$</span>
                  {plan === 'ANNUAL' ? annualPrice : monthlyPrice}
                </>
              )}
            </div>
            <div className="pricing-option_inner_top_price_label">
              {isDiscountActive
                ? plan === 'ANNUAL'
                  ? 'this year'
                  : activeDiscount?.discount?.durationMonths &&
                      activeDiscount.discount.durationMonths > 1
                    ? `for ${activeDiscount.discount.durationMonths} months`
                    : 'this month'
                : plan === 'ANNUAL'
                  ? 'per year'
                  : 'per month'}
            </div>
            {isDiscountActive && (
              <div className="savings">
                <p>
                  ${plan === 'ANNUAL' ? annualPrice : monthlyPrice} per{' '}
                  {plan === 'ANNUAL' ? 'year' : 'month'}{' '}
                  {plan === 'ANNUAL'
                    ? 'afterwards'
                    : activeDiscount?.discount?.durationMonths
                      ? `after ${activeDiscount?.discount?.durationMonths} months`
                      : 'after'}
                </p>
              </div>
            )}
          </div>
          {renderCheckoutButton()}
        </div>
        <div className="pricing-option_inner_bottom">
          {renderFeatures(features, guarantee)}
        </div>
        {product === primaryExamAccessLevel &&
          !isUpgrading &&
          !hasSubscription && <div className="gradient-blur"></div>}
      </div>
      {product === primaryExamAccessLevel &&
        !isUpgrading &&
        !hasSubscription && (
          <>
            <img className="light-streak" src={LightStreak} alt="" />
            <img
              className="light-streak light-streak--2"
              src={LightStreak}
              alt=""
            />
            <img
              className="light-streak light-streak--3"
              src={LightStreak}
              alt=""
            />
          </>
        )}
    </div>
  );
};

export default PricingOption;
